import React from 'react';
import {
  Box,
  Field,
  HStack,
  Image,
  Menu,
  Portal,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as InputErrorIcon } from 'assets/icons/input_error.svg';
import { colors } from 'theme';

const surfaceBorder = 'rgba(255,255,255,0.08)';

const menuListScrollCss = {
  'color-scheme': 'dark',
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.28) rgba(255,255,255,0.06)',
  '&::-webkit-scrollbar': {
    width: '10px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.28)',
    borderRadius: '6px',
    border: '2px solid rgba(0,0,0,0.15)',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },
} as const;

export type PayWithOption = {
  label: string;
  value: string;
  icon: string;
};

type PayWithSelectProps = {
  label: string;
  error?: string;
  isRequired?: boolean;
  options: PayWithOption[];
  value: string;
  onChange: (_nextValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

const PayWithSelect: React.FC<PayWithSelectProps> = ({
  label,
  error,
  isRequired = false,
  options,
  value,
  onChange,
  placeholder = 'Select',
  disabled = false,
}) => {
  const selected = options.find((o) => o.value === value);

  return (
    <Field.Root invalid={Boolean(error)} required={isRequired}>
      <Field.Label
        fontWeight="medium"
        color={error ? 'error' : 'secondary2'}
        mb={2}
      >
        {label}
      </Field.Label>
      <Box position="relative" w="100%">
        <Box position="relative" w="100%" mb={error ? 6 : 4}>
          <Menu.Root>
            <Menu.Trigger
              unstyled
              type="button"
              disabled={disabled || options.length === 0}
              width="100%"
              borderRadius="lg"
              minH="12"
              px={4}
              py={3}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              cursor={
                disabled || options.length === 0 ? 'not-allowed' : 'pointer'
              }
              opacity={options.length === 0 ? 0.6 : 1}
              color="body"
              bg="secondary"
              borderWidth="1px"
              borderStyle="solid"
              borderColor={error ? 'error' : surfaceBorder}
              _hover={{
                bg:
                  disabled || options.length === 0
                    ? 'secondary'
                    : 'rgba(255,255,255,0.05)',
              }}
              _focusVisible={{
                outline: 'none',
                boxShadow: '0 0 0 2px rgba(62, 207, 142, 0.45)',
              }}
              css={{
                backgroundColor: `${colors.secondary} !important`,
                color: `${colors.body} !important`,
              }}
            >
              {selected ? (
                <HStack gap={2} w="100%" justify="flex-start" align="center">
                  <Image
                    boxSize={7}
                    rounded="full"
                    src={selected.icon}
                    alt=""
                    flexShrink={0}
                    objectFit="cover"
                  />
                  <Text
                    fontSize="md"
                    fontWeight="medium"
                    color="body"
                    lineClamp={1}
                    flex={1}
                    minW={0}
                    textAlign="left"
                  >
                    {selected.label}
                  </Text>
                  <Text fontSize="sm" color="body2" flexShrink={0}>
                    {selected.value}
                  </Text>
                </HStack>
              ) : (
                <Text fontSize="md" color="body2" textAlign="left">
                  {placeholder}
                </Text>
              )}
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content
                  maxH="280px"
                  overflowY="auto"
                  bg="dark"
                  borderWidth="1px"
                  borderColor={surfaceBorder}
                  borderRadius="lg"
                  py={1}
                  boxShadow="0 12px 40px rgba(0,0,0,0.45)"
                  css={menuListScrollCss}
                >
                  {options.map((opt) => (
                    <Menu.Item
                      key={opt.value}
                      value={opt.value}
                      minH="10"
                      onSelect={() => onChange(opt.value)}
                      py={3}
                      px={4}
                      borderRadius="md"
                      _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                    >
                      <HStack gap={2} flex={1} w="100%">
                        <Image
                          boxSize={6}
                          rounded="full"
                          src={opt.icon}
                          alt=""
                          flexShrink={0}
                          objectFit="cover"
                        />
                        <Text fontSize="sm" color="body" flex={1} minW={0}>
                          {opt.label}
                        </Text>
                        <Text fontSize="xs" color="body2" flexShrink={0}>
                          {opt.value}
                        </Text>
                      </HStack>
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Box>
        {error ? (
          <Box
            color="error"
            fontSize="sm"
            textAlign="start"
            display="flex"
            alignItems="center"
            gap={1}
            pos="absolute"
            left={0}
            bottom={0}
          >
            <InputErrorIcon />
            {error}
          </Box>
        ) : null}
      </Box>
    </Field.Root>
  );
};

export default PayWithSelect;
